(() => {
  const money = (amount, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
    style: 'currency', currency: currency || 'GBP'
  }).format(amount / 100);

  const rugDimensions = (value) => {
    const numbers = String(value || '').match(/\d+(?:\.\d+)?/g)?.map(Number) || [];
    if (numbers.length < 1) return { width: 55, ratio: 1.45, short: 1, long: 1, isRound: false };
    const isRound = /round|circle|ø/i.test(value);
    const short = Math.min(numbers[0], numbers[1] || numbers[0]);
    const long = Math.max(numbers[0], numbers[1] || numbers[0]);
    return { width: Math.min(84, Math.max(28, (long / 300) * 84)), ratio: isRound ? 1 : Math.max(.42, Math.min(4.5, long / short)), short, long, isRound };
  };

  const rugSizeKind = (value) => {
    const dimensions = rugDimensions(value);
    if (dimensions.isRound) return 'round';
    if (/runner/i.test(value) || dimensions.ratio >= 1.95) return 'runner';
    return 'area';
  };

  const imperialSize = (value) => {
    const dimensions = rugDimensions(value);
    const toFeetAndInches = (centimetres) => {
      let totalInches = Math.round(centimetres / 2.54);
      const feet = Math.floor(totalInches / 12);
      totalInches %= 12;
      if (!feet) return `${totalInches}\u2033`;
      return `${feet}\u2032${totalInches ? ` ${totalInches}\u2033` : ''}`;
    };
    if (!dimensions.short) return '';
    if (dimensions.isRound) return `\u2248 ${toFeetAndInches(dimensions.short)} diameter`;
    return `\u2248 ${toFeetAndInches(dimensions.short)} \u00d7 ${toFeetAndInches(dimensions.long)}`;
  };

  const init = (root) => {
    const variants = JSON.parse(root.querySelector('[data-product-variants]')?.textContent || '[]');
    const dialog = root.querySelector('[data-variant-dialog]');
    const form = root.querySelector('.nl-product-form');
    if (!form || !variants.length) return;
    root.dataset.productReady = 'true';

    let selected = variants.find((variant) => variant.id === Number(form.querySelector('[data-variant-id]')?.value)) || variants[0];
    let options = [...selected.options];
    const currency = root.dataset.currency || 'GBP';
    const variantInput = form.querySelector('[data-variant-id]');
    const syncVariantInput = () => {
      if (!variantInput || !selected) return;
      variantInput.value = String(selected.id);
      variantInput.setAttribute('value', String(selected.id));
    };

    root.querySelectorAll('[data-rug-size-groups]').forEach((groups) => {
      const source = groups.querySelector('[data-rug-size-source]');
      if (!source) return;
      [...source.querySelectorAll('.nl-option-value--rug-size')].forEach((button) => {
        const value = button.dataset.optionValue || '';
        const kind = rugSizeKind(value);
        button.dataset.sizeKind = kind;
        button.querySelector('[data-size-preview]')?.classList.add(`nl-size-preview--${kind}`);
        button.querySelector('[data-size-imperial]')?.replaceChildren(document.createTextNode(imperialSize(value)));
        groups.querySelector(`[data-size-group-grid="${kind}"]`)?.append(button);
      });
      groups.querySelectorAll('[data-size-group-panel]').forEach((panel) => {
        panel.hidden = !panel.querySelector('.nl-option-value--rug-size');
      });
      groups.classList.add('is-organised');
    });

    const render = () => {
      selected = variants.find((variant) => variant.options.every((value, index) => value === options[index]));
      root.querySelectorAll('[data-option-group]').forEach((group) => {
        const index = Number(group.dataset.optionIndex);
        group.querySelector('[data-option-current]')?.replaceChildren(document.createTextNode(options[index] || ''));
        group.querySelectorAll('[data-option-value]').forEach((button) => {
          const active = button.dataset.optionValue === options[index];
          button.classList.toggle('is-selected', active);
          button.setAttribute('aria-pressed', String(active));
          const matchingVariant = variants.find((variant) => variant.options[index] === button.dataset.optionValue && variant.options.every((value, i) => i === index || value === options[i]));
          const possible = Boolean(matchingVariant?.available);
          button.classList.toggle('is-unavailable', !possible);
          button.disabled = !possible;
          button.setAttribute('aria-disabled', String(!possible));
          const optionPrice = button.querySelector('.nl-size-option__price');
          if (optionPrice && matchingVariant) optionPrice.textContent = money(matchingVariant.price, currency);
        });
      });
      root.querySelector('[data-selection-summary]')?.replaceChildren(document.createTextNode(options.join(' · ')));
      if (!selected) return;
      syncVariantInput();
      root.querySelector('[data-product-price]')?.replaceChildren(document.createTextNode(money(selected.price, currency)));
      root.querySelector('[data-add-price]')?.replaceChildren(document.createTextNode(money(selected.price, currency)));
      root.querySelector('[data-drawer-price]')?.replaceChildren(document.createTextNode(money(selected.price, currency)));
      if (selected.featured_image?.src) {
        const leadImage = root.querySelector('.nl-product-gallery__item--lead img');
        if (leadImage) {
          leadImage.src = selected.featured_image.src;
          leadImage.removeAttribute('srcset');
        }
      }
      const compare = root.querySelector('[data-product-compare]');
      if (compare) {
        compare.hidden = !(selected.compare_at_price > selected.price);
        compare.textContent = selected.compare_at_price ? money(selected.compare_at_price, currency) : '';
      }
      root.querySelectorAll('[data-add-to-cart], [data-drawer-add]').forEach((button) => button.disabled = !selected.available);
      root.querySelector('[data-add-label]')?.replaceChildren(document.createTextNode(selected.available ? 'Add to basket' : 'Sold out'));
      root.querySelector('[data-drawer-label]')?.replaceChildren(document.createTextNode(selected.available ? 'Add selected option' : 'Sold out'));
      const url = new URL(window.location.href); url.searchParams.set('variant', selected.id); window.history.replaceState({}, '', url);
    };

    root.querySelectorAll('[data-option-value]').forEach((button) => button.addEventListener('click', () => {
      const group = button.closest('[data-option-group]');
      options[Number(group.dataset.optionIndex)] = button.dataset.optionValue;
      render();
    }));
    form.addEventListener('submit', syncVariantInput);
    const drawerTrigger = root.querySelector('[data-open-variant-drawer]');
    const setDrawer = (open) => {
      if (!dialog) return;
      dialog.classList.toggle('is-open', open);
      dialog.setAttribute('aria-hidden', String(!open));
      drawerTrigger?.setAttribute('aria-expanded', String(open));
      document.documentElement.classList.toggle('nl-drawer-open', open);
      if (open) dialog.querySelector('.nl-drawer__close')?.focus();
      else drawerTrigger?.focus();
    };
    drawerTrigger?.addEventListener('click', () => setDrawer(true));
    dialog?.querySelectorAll('[data-close-variant-drawer]').forEach((button) => button.addEventListener('click', () => setDrawer(false)));
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && dialog?.classList.contains('is-open')) setDrawer(false); });
    render();
  };

  const run = () => document.querySelectorAll('[data-netline-product]').forEach(init);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', run) : run();
})();
